// React Router
import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material";
import { SECTION_BORDER_PIXEL } from "../../constants/section";
import { products } from "../../data/products";
import { updatePageSEO, generateProductSEO } from "../../utils/seo";
// import { Category } from "../../types/product";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Get the category from URL params to navigate back correctly
  const categoryParam = searchParams.get("category");
  // Find product by id from real data
  const product = products.find((p) => p.id === id);

  // Update SEO for product detail page
  useEffect(() => {
    if (product) {
      const productSEO = generateProductSEO(
        product.name,
        product.category,
        product.description,
        product.price
      );
      updatePageSEO(productSEO);
    }
  }, [product]);

  // // Get side options for current product category
  // const sideOptions = product ? sideByCategory[product.category] : null;
  // const allSideItems = products.filter((p) => p.category === Category.SIDE);

  // // Helper function to get side item details
  // const getSideItemDetails = (sideId: string) => {
  //   return allSideItems.find((item) => item.id === sideId);
  // };

  // Get previous and next products (only visible ones)
  const visibleProducts = products.filter((p) => p.isVisible);
  const currentVisibleIndex = visibleProducts.findIndex((p) => p.id === id);
  const previousProduct =
    currentVisibleIndex > 0 ? visibleProducts[currentVisibleIndex - 1] : null;
  const nextProduct =
    currentVisibleIndex < visibleProducts.length - 1
      ? visibleProducts[currentVisibleIndex + 1]
      : null;

  const handlePrevious = () => {
    if (previousProduct) {
      const categoryQuery = categoryParam ? `?category=${categoryParam}` : "";
      navigate(`/products/${previousProduct.id}${categoryQuery}`);
    }
  };

  const handleNext = () => {
    if (nextProduct) {
      const categoryQuery = categoryParam ? `?category=${categoryParam}` : "";
      navigate(`/products/${nextProduct.id}${categoryQuery}`);
    }
  };

  const handleBackToProducts = () => {
    if (categoryParam) {
      // Navigate back to products page with the category preserved as URL parameter
      navigate(`/products?category=${categoryParam}`);
    } else {
      navigate("/products");
    }
  };

  // Handle image scroll for mobile carousel
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const container = event.currentTarget;
    const scrollLeft = container.scrollLeft;
    const itemWidth = container.scrollWidth / (product?.images?.length || 1);
    const newIndex = Math.round(scrollLeft / itemWidth);
    setCurrentImageIndex(newIndex);
  };

  // Fallback if product not found
  if (!product) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Typography variant="h4">Product not found</Typography>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Box sx={{ width: "100%", position: "relative" }}>
        {/* Desktop Layout */}
        <Box
          sx={{
            display: { xs: "none", md: "block" },
          }}
        >
          {/* Left Image Section */}
          <Box
            sx={{
              width: "50%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Product Images */}
            {product.images && product.images.length > 0 ? (
              product.images.map((image, index) => (
                <Box
                  key={index}
                  sx={{
                    height: "100vh",
                    border: `${SECTION_BORDER_PIXEL} solid #000`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f9fafb",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={image}
                    alt={`${product.name} - Image ${index + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              ))
            ) : (
              <Box
                sx={{
                  height: "100vh",
                  border: `${SECTION_BORDER_PIXEL} solid #000`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f9fafb",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div>No Image Available</div>
              </Box>
            )}
          </Box>

          {/* Right Fixed Information Section - Desktop Only */}
          <Box
            sx={{
              width: "50%",
              height: "100vh",
              border: `${SECTION_BORDER_PIXEL} solid #000`,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              backgroundColor: "#ffffff",
              padding: "60px 40px",
              position: "fixed",
              top: 0,
              right: 0,
            }}
          >
            {/* Back Button */}
            <Button
              variant="text"
              onClick={handleBackToProducts}
              sx={{
                color: "#666",
                fontSize: "12px",
                fontWeight: "500",
                textTransform: "none",
                padding: "4px 0",
                marginBottom: "16px",
                justifyContent: "flex-start",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "#000",
                },
              }}
            >
              ← Back to Products
            </Button>

            {/* Category */}
            <Typography
              variant="body2"
              sx={{
                fontSize: "12px",
                fontWeight: "500",
                color: "#666",
                marginBottom: "8px",
                letterSpacing: "1px",
              }}
            >
              {product.category}
            </Typography>

            {/* Product Name */}
            <Typography
              variant="h1"
              sx={{
                fontSize: "36px",
                fontWeight: "bold",
                color: "#000",
                marginBottom: "12px",
                lineHeight: "1.2",
                fontFamily: "'Barlow Condensed', sans-serif",
              }}
            >
              {product.name}
            </Typography>

            {/* Price */}
            {product.price && (
              <Typography
                variant="h2"
                sx={{
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#000",
                  marginBottom: "32px",
                }}
              >
                {product.price} €
              </Typography>
            )}

            {/* Description */}
            <Typography
              variant="body1"
              sx={{
                fontSize: "14px",
                lineHeight: "1.6",
                color: "#666",
                marginBottom: "32px",
                maxWidth: "400px",
              }}
            >
              {product.description}
            </Typography>

            {/* Side Selection */}
            {/* {sideOptions &&
              (sideOptions.cold.length > 0 || sideOptions.hot.length > 0) && (
                <Box sx={{ marginBottom: "32px" }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      color: "#000",
                      marginBottom: "16px",
                      textAlign: "left",
                    }}
                  >
                    2 Accompagnements au choix 5€
                  </Typography>

                  <Box sx={{ display: "flex", gap: "24px" }}>
                    {sideOptions.hot.length > 0 && (
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h4"
                          sx={{
                            fontSize: "14px",
                            fontWeight: "bold",
                            color: "#999",
                            marginBottom: "12px",
                          }}
                        >
                          Chaud
                        </Typography>
                        {sideOptions.hot.map((sideId) => {
                          const sideItem = getSideItemDetails(sideId);
                          return sideItem ? (
                            <Box
                              key={sideId}
                              onClick={() => navigate(`/products/${sideId}`)}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginBottom: "8px",
                                cursor: "pointer",
                                padding: "4px",
                                borderRadius: "4px",
                                "&:hover": {
                                  backgroundColor: "#f5f5f5",
                                },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  color: "#666",
                                  lineHeight: "1.4",
                                }}
                              >
                                {sideItem.name}
                              </Typography>
                            </Box>
                          ) : null;
                        })}
                      </Box>
                    )}

                    {sideOptions.cold.length > 0 && (
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h4"
                          sx={{
                            fontSize: "14px",
                            fontWeight: "bold",
                            color: "#999",
                            marginBottom: "12px",
                          }}
                        >
                          Froid
                        </Typography>
                        {sideOptions.cold.map((sideId) => {
                          const sideItem = getSideItemDetails(sideId);
                          return sideItem ? (
                            <Box
                              key={sideId}
                              onClick={() => navigate(`/products/${sideId}`)}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginBottom: "8px",
                                cursor: "pointer",
                                padding: "4px",
                                borderRadius: "4px",
                                "&:hover": {
                                  backgroundColor: "#f5f5f5",
                                },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  color: "#666",
                                  lineHeight: "1.4",
                                }}
                              >
                                {sideItem.name}
                              </Typography>
                            </Box>
                          ) : null;
                        })}
                      </Box>
                    )}
                  </Box>
                </Box>
              )} */}

            {/* Navigation Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Button
                variant="text"
                onClick={handlePrevious}
                disabled={!previousProduct}
                sx={{
                  color: previousProduct ? "#666" : "#ccc",
                  fontSize: "12px",
                  fontWeight: "500",
                  textTransform: "none",
                  padding: "8px 0",
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: previousProduct ? "#000" : "#ccc",
                  },
                  "&:disabled": {
                    color: "#ccc",
                  },
                }}
              >
                ← PREVIOUS
              </Button>

              <Button
                variant="text"
                onClick={handleNext}
                disabled={!nextProduct}
                sx={{
                  color: nextProduct ? "#666" : "#ccc",
                  fontSize: "12px",
                  fontWeight: "500",
                  textTransform: "none",
                  padding: "8px 0",
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: nextProduct ? "#000" : "#ccc",
                  },
                  "&:disabled": {
                    color: "#ccc",
                  },
                }}
              >
                NEXT →
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Mobile Layout */}
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            position: "relative",
          }}
        >
          {/* Horizontal Scrolling Images */}
          <Box
            sx={{
              display: "flex",
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
            onScroll={handleScroll}
          >
            {/* Product Images Mobile */}
            {product.images && product.images.length > 0 ? (
              product.images.map((image, index) => (
                <Box
                  key={index}
                  sx={{
                    minWidth: "100vw",
                    height: "70vh",
                    border: `${SECTION_BORDER_PIXEL} solid #000`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f9fafb",
                    scrollSnapAlign: "start",
                    position: "relative",
                  }}
                >
                  <img
                    src={image}
                    alt={`${product.name} - Image ${index + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              ))
            ) : (
              <Box
                sx={{
                  minWidth: "100vw",
                  height: "70vh",
                  border: `${SECTION_BORDER_PIXEL} solid #000`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f9fafb",
                  scrollSnapAlign: "start",
                  position: "relative",
                }}
              >
                <div>No Image Available</div>
              </Box>
            )}
          </Box>

          {/* Pagination Dots for Mobile */}
          {product.images && product.images.length > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "16px 0",
                gap: "8px",
                position: "absolute",
                bottom: "20px",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 10,
              }}
            >
              {product.images.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor:
                      currentImageIndex === index ? "#888888" : "#ccc",
                    transition: "background-color 0.3s ease",
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Mobile Information Section Below Images */}
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            border: `${SECTION_BORDER_PIXEL} solid #000`,
            backgroundColor: "#ffffff",
            padding: "40px 20px",
          }}
        >
          {/* Back Button Mobile */}
          <Button
            variant="text"
            onClick={handleBackToProducts}
            sx={{
              color: "#666",
              fontSize: "12px",
              fontWeight: "500",
              textTransform: "none",
              padding: "4px 0",
              marginBottom: "16px",
              justifyContent: "flex-start",
              "&:hover": {
                backgroundColor: "transparent",
                color: "#000",
              },
            }}
          >
            ← Back to Products
          </Button>

          {/* Category */}
          <Typography
            variant="body2"
            sx={{
              fontSize: "12px",
              fontWeight: "500",
              color: "#666",
              marginBottom: "8px",
              letterSpacing: "1px",
            }}
          >
            {product.category}
          </Typography>

          {/* Product Name */}
          <Typography
            variant="h1"
            sx={{
              fontSize: "28px",
              fontWeight: "bold",
              color: "#000",
              marginBottom: "12px",
              lineHeight: "1.2",
              fontFamily: "'Barlow Condensed', sans-serif",
            }}
          >
            {product.name}
          </Typography>

          {/* Price */}
          <Typography
            variant="h2"
            sx={{
              fontSize: "20px",
              fontWeight: "bold",
              color: "#000",
              marginBottom: "24px",
            }}
          >
            {product.price} €
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              fontSize: "14px",
              lineHeight: "1.6",
              color: "#666",
              marginBottom: "24px",
            }}
          >
            {product.description}
          </Typography>

          {/* Side Selection - Mobile */}
          {/* {sideOptions &&
            (sideOptions.cold.length > 0 || sideOptions.hot.length > 0) && (
              <Box sx={{ marginBottom: "24px" }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    color: "#000",
                    marginBottom: "16px",
                    textAlign: "left",
                  }}
                >
                  2 Accompagnements au choix 5€
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: "20px",
                  }}
                >
                  {sideOptions.hot.length > 0 && (
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          color: "#999",
                          marginBottom: "12px",
                        }}
                      >
                        Chaud
                      </Typography>
                      {sideOptions.hot.map((sideId) => {
                        const sideItem = getSideItemDetails(sideId);
                        return sideItem ? (
                          <Box
                            key={sideId}
                            onClick={() => navigate(`/products/${sideId}`)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginBottom: "8px",
                              cursor: "pointer",
                              padding: "4px",
                              borderRadius: "4px",
                              "&:hover": {
                                backgroundColor: "#f5f5f5",
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#666",
                                lineHeight: "1.4",
                              }}
                            >
                              {sideItem.name}
                            </Typography>
                          </Box>
                        ) : null;
                      })}
                    </Box>
                  )}

                  {sideOptions.cold.length > 0 && (
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          color: "#999",
                          marginBottom: "12px",
                        }}
                      >
                        Froid
                      </Typography>
                      {sideOptions.cold.map((sideId) => {
                        const sideItem = getSideItemDetails(sideId);
                        return sideItem ? (
                          <Box
                            key={sideId}
                            onClick={() => navigate(`/products/${sideId}`)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginBottom: "8px",
                              cursor: "pointer",
                              padding: "4px",
                              borderRadius: "4px",
                              "&:hover": {
                                backgroundColor: "#f5f5f5",
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#666",
                                lineHeight: "1.4",
                              }}
                            >
                              {sideItem.name}
                            </Typography>
                          </Box>
                        ) : null;
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
            )} */}

          {/* Navigation Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Button
              variant="text"
              onClick={handlePrevious}
              disabled={!previousProduct}
              sx={{
                color: previousProduct ? "#666" : "#ccc",
                fontSize: "12px",
                fontWeight: "500",
                textTransform: "none",
                padding: "8px 0",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: previousProduct ? "#000" : "#ccc",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              ← PREVIOUS
            </Button>

            <Button
              variant="text"
              onClick={handleNext}
              disabled={!nextProduct}
              sx={{
                color: nextProduct ? "#666" : "#ccc",
                fontSize: "12px",
                fontWeight: "500",
                textTransform: "none",
                padding: "8px 0",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: nextProduct ? "#000" : "#ccc",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              NEXT →
            </Button>
          </Box>
        </Box>
      </Box>
    </div>
  );
}
